import api from './axiosInstance';
import { User, CreateUserRequest, UpdateUserRequest, UserFilterParams } from '../types/user';

// Standardized API Response Interface
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

interface PaginatedResponse<T> {
  success: boolean;
  message: string;
  data: T[];
  meta: {
    page: number;
    limit: number;
    totalPages: number;
    totalDocs: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
  };
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

export const userService = {
  // Get all users with role information
  getUsers: async (params?: UserFilterParams): Promise<{ users: User[]; totalDocs: number; pagination?: any }> => {
    try {
      const response = await api.get<PaginatedResponse<User>>('/users', { params });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch users');
      }

      return {
        users: response.data.data,
        totalDocs: response.data.meta.totalDocs,
        pagination: {
          page: response.data.meta.page,
          limit: response.data.meta.limit,
          totalPages: response.data.meta.totalPages,
          hasNextPage: response.data.meta.hasNextPage,
          hasPrevPage: response.data.meta.hasPrevPage,
          nextPage: response.data.meta.nextPage,
          prevPage: response.data.meta.prevPage,
        }
      };
    } catch (error: any) {
      console.error('Error fetching users:', error);
      throw new Error(error.message || 'Failed to fetch users');
    }
  },

  // Get user by ID
  getUserById: async (id: string): Promise<User> => {
    try {
      const response = await api.get<ApiResponse<User>>(`/users/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch user');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching user:', error);
      throw new Error(error.message || 'Failed to fetch user');
    }
  },

  // Create new user
  createUser: async (userData: CreateUserRequest): Promise<User> => {
    try {
      // Validate required fields
      if (!userData.name || !userData.role) {
        throw new Error('Name and role are required');
      }

      if (!userData.email && !userData.mobile) {
        throw new Error('Either email or mobile number is required');
      }

      // Use the /auth/create-user endpoint which provides welcome emails and password setup
      const response = await api.post<ApiResponse<User>>('/auth/create-user', userData);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create user');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Error creating user:', error);
      throw new Error(error.message || 'Failed to create user');
    }
  },

  // Update user
  updateUser: async (id: string, userData: UpdateUserRequest): Promise<User> => {
    try {
      const response = await api.patch<ApiResponse<User>>(`/users/${id}`, userData);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update user');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Error updating user:', error);
      throw new Error(error.message || 'Failed to update user');
    }
  },

  // Delete user
  deleteUser: async (id: string): Promise<void> => {
    try {
      const response = await api.delete<ApiResponse<null>>(`/users/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete user');
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      throw new Error(error.message || 'Failed to delete user');
    }
  },

  // Toggle user status
  toggleUserStatus: async (id: string): Promise<User> => {
    try {
      const response = await api.patch<ApiResponse<User>>(`/users/${id}/toggle`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to toggle user status');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Error toggling user status:', error);
      throw new Error(error.message || 'Failed to toggle user status');
    }
  },

  // Get users with role details (using populate)
  getUsersWithRoles: async (params?: UserFilterParams): Promise<{ users: User[]; totalDocs: number; pagination?: any }> => {
    try {
      const queryParams = { ...params, populate: 'role' };
      const response = await api.get<PaginatedResponse<User>>('/users', {
        params: queryParams
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch users with roles');
      }

      return {
        users: response.data.data,
        totalDocs: response.data.meta.totalDocs,
        pagination: {
          page: response.data.meta.page,
          limit: response.data.meta.limit,
          totalPages: response.data.meta.totalPages,
          hasNextPage: response.data.meta.hasNextPage,
          hasPrevPage: response.data.meta.hasPrevPage,
          nextPage: response.data.meta.nextPage,
          prevPage: response.data.meta.prevPage,
        }
      };
    } catch (error: any) {
      console.error('Error fetching users with roles:', error);
      throw new Error(error.message || 'Failed to fetch users with roles');
    }
  },

  // Create user through admin endpoint (if needed)
  createUserByAdmin: async (userData: CreateUserRequest): Promise<User> => {
    try {
      const response = await api.post<ApiResponse<User>>('/auth/create-user', userData);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create user');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Error creating user by admin:', error);
      throw new Error(error.message || 'Failed to create user');
    }
  },

  // Utility function to validate user data
  validateUserData: (userData: CreateUserRequest | UpdateUserRequest): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if ('name' in userData && userData.name && userData.name.trim().length < 2) {
      errors.push('Name must be at least 2 characters long');
    }

    if ('email' in userData && userData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
      errors.push('Please provide a valid email address');
    }

    if ('mobile' in userData && userData.mobile && !/^\d{10}$/.test(userData.mobile)) {
      errors.push('Mobile number must be exactly 10 digits');
    }

    if ('password' in userData && userData.password) {
      if (userData.password.length < 8) {
        errors.push('Password must be at least 8 characters long');
      }
      if (!/(?=.*[a-z])/.test(userData.password)) {
        errors.push('Password must contain at least one lowercase letter');
      }
      if (!/(?=.*[A-Z])/.test(userData.password)) {
        errors.push('Password must contain at least one uppercase letter');
      }
      if (!/(?=.*\d)/.test(userData.password)) {
        errors.push('Password must contain at least one number');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Get project-wise assigned resources with task information
  getProjectResources: async (projectId: string): Promise<{
    totalResources: number;
    totalTasks: number;
    assignedTasks: number;
    unassignedTasks: number;
    completedTasks: number;
    resourcesByRole: Record<string, Array<{
      _id: string;
      name: string;
      email?: string;
      mobile?: string;
      role: string;
      projectStats: {
        totalTasks: number;
        assignedTasks: number;
        inProgressTasks: number;
        completedTasks: number;
        dueTasks: number;
        dateRange?: {
          from: string;
          to: string;
        };
      };
    }>>;
  }> => {
    try {
      const response = await api.get<ApiResponse<any>>(`/users/project/${projectId}/resources`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch project resources');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching project resources:', error);
      throw new Error(error.message || 'Failed to fetch project resources');
    }
  },

  // Get all users with approver roles, optionally filtered by department (OMS/AMS/EPC)
  getApproverUsers: async (department?: 'OMS' | 'AMS' | 'EPC'): Promise<User[]> => {
    try {
      const response = await api.get<ApiResponse<User[]>>('/users/approvers', {
        params: department ? { department } : undefined,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch approver users');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching approver users:', error);
      throw new Error(error.message || 'Failed to fetch approver users');
    }
  },

  downloadTemplate: async (): Promise<void> => {
    try {
      const response = await api.get('/users/bulk-upload/template', {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'user_bulk_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      console.error('Error downloading template:', error);
      throw new Error(error.message || 'Failed to download template');
    }
  },

  bulkUpload: async (file: File): Promise<any> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/users/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to upload users');
      }
      return response.data;
    } catch (error: any) {
      console.error('Error uploading users:', error);
      throw new Error(error.message || 'Failed to upload users');
    }
  },
}; 